import { INTRANET_FEEDBACK_NOTE_MAX, type IntranetContentDetail } from '@gb-ticket/shared-types';
import { ThumbsDownIcon, ThumbsUpIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ApiRequestError } from '@/lib/api';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { useSubmitIntranetFeedback } from '@/products/intranet/features/intranet/use-intranet-library';

/** „War das hilfreich?“ — one vote per person; the optional note goes to the editorial team only. */
export function FeedbackWidget({ content }: { content: IntranetContentDetail }) {
  const copy = useVoiceCopy();
  const submit = useSubmitIntranetFeedback(content.slug);
  const own = content.myFeedback;
  const [note, setNote] = useState(own?.note ?? '');

  async function send(helpful: boolean) {
    try {
      await submit.mutateAsync({ helpful, note: note.trim() || null });
      toast.success(copy('intranet.feedback.thanks'));
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Senden fehlgeschlagen.');
    }
  }

  return (
    <section
      aria-labelledby="hilfreich"
      className="space-y-3 rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5"
    >
      <h2 id="hilfreich" className="font-semibold text-primary">
        War das hilfreich?
      </h2>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant={own?.helpful === true ? 'default' : 'outline'}
          aria-pressed={own?.helpful === true}
          disabled={submit.isPending}
          onClick={() => void send(true)}
        >
          <ThumbsUpIcon aria-hidden />
          Ja ({content.feedback.helpful})
        </Button>
        <Button
          type="button"
          variant={own?.helpful === false ? 'default' : 'outline'}
          aria-pressed={own?.helpful === false}
          disabled={submit.isPending}
          onClick={() => void send(false)}
        >
          <ThumbsDownIcon aria-hidden />
          Nein ({content.feedback.notHelpful})
        </Button>
      </div>
      {own ? (
        <div className="space-y-2">
          <Label htmlFor="intranet-feedback-note">
            Was können wir verbessern? (nur für die Redaktion sichtbar)
          </Label>
          <Textarea
            id="intranet-feedback-note"
            rows={2}
            maxLength={INTRANET_FEEDBACK_NOTE_MAX}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {note.length}/{INTRANET_FEEDBACK_NOTE_MAX}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={submit.isPending || note.trim() === (own.note ?? '')}
              onClick={() => void send(own.helpful)}
            >
              Rückmeldung senden
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
